import { useState } from 'react';
import { ErrorBanner, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { downloadCsv } from '../../lib/download';
import { readSpreadsheet } from '../../lib/spreadsheet';

/**
 * Bulk client import from a spreadsheet.
 *
 * THE TEMPLATE AND THE READER ARE THE SAME LIST. Every column below carries
 * its own header, its example, and the function that turns a cell into the
 * field the API wants, so the file somebody is handed and the file this can
 * read cannot drift apart — which is the usual way a template stops being
 * true.
 *
 * .csv AND .xlsx both, through lib/spreadsheet.ts: telling somebody who lives
 * in Excel to "save as CSV first" is a step that exists only because the
 * reader was lazy.
 *
 * HEADERS ARE MATCHED LOOSELY — case, spaces and punctuation ignored — so
 * "Monthly revenue", "monthly_revenue" and "MONTHLY REVENUE" are one column.
 * A file saved out of a different system should not have to be retyped to be
 * read. Columns that are not recognised are listed rather than dropped in
 * silence, and only Name is required.
 */

type Row = Record<string, unknown>;

interface Column {
  /** The header written into the template. */
  header: string;
  /** What goes in the template's example row. */
  example: string;
  /** The key the API expects. */
  field: string;
  /** Cell text to the value the API wants, or an error for this row. */
  parse?: (cell: string) => { value: unknown } | { error: string };
  required?: boolean;
  hint?: string;
}

const number = (what: string, max?: number) => (cell: string) => {
  /* A spreadsheet writes money as "$1,250.00" and nobody should have to
     clean that up by hand before the file will load. */
  const n = Number(cell.replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n) || n < 0) return { error: `${what} is not a number: "${cell}"` };
  if (max !== undefined && n > max) return { error: `${what} must be ${max} or less: "${cell}"` };
  return { value: n };
};

const COLUMNS: Column[] = [
  { header: 'Name', example: 'Northwind Traders', field: 'name', required: true, hint: 'Required. The company name, exactly as it should read here.' },
  { header: 'Industry', example: 'Manufacturing', field: 'industry' },
  { header: 'Website', example: 'northwind.example.com', field: 'website' },
  { header: 'Primary contact', example: 'Dana Reyes', field: 'primaryContact', hint: 'Also added as the first stakeholder.' },
  { header: 'Email', example: 'dana@northwind.example.com', field: 'email' },
  { header: 'Phone', example: '303-555-0142', field: 'phone' },
  { header: 'Address', example: '400 Main St, Denver, CO 80202', field: 'address' },
  { header: 'Status', example: 'Client', field: 'status', hint: 'Client, Prospect, At Risk, Inactive or Churned. Anything else becomes Client.' },
  { header: 'Monthly revenue', example: '4250', field: 'monthlyRevenue', parse: number('Monthly revenue'), hint: 'A number. Currency symbols and commas are fine.' },
  { header: 'Contract type', example: 'Managed services', field: 'contractType' },
  { header: 'Health score', example: '75', field: 'healthScore', parse: number('Health score', 100), hint: '0 to 100. Left out, a new client starts at 75.' },
  { header: 'Account manager', example: 'barbara@cmitsolutions.com', field: 'accountManager', hint: 'A name or an email from the directory. Unknown, the client arrives unassigned.' },
  { header: 'Notes', example: 'Moved over from the old spreadsheet.', field: 'notes' },
];

/** Case, spaces and punctuation are not part of a column's identity. */
const key = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, '');
const BY_KEY = new Map(COLUMNS.map((c) => [key(c.header), c]));

export interface ClientImportResult {
  created: number;
  skipped: number;
  rows: { row: number; name: string; outcome: 'created' | 'skipped'; reason?: string; note?: string }[];
}

interface Parsed {
  fileName: string;
  rows: Row[];
  /** Headers in the file that match no column, named so they are not a mystery. */
  unknown: string[];
  /** What to expect, found before anything is sent. `dropped` is the
   *  difference between a row that will not arrive and a cell that will
   *  simply be empty when it does. */
  problems: { row: number; message: string; dropped: boolean }[];
  /** Rows with no name at all are dropped here rather than at the API. */
  blank: number;
}

export function AmClientImport({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [result, setResult] = useState<ClientImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const template = () =>
    downloadCsv('client_import_template.csv', [COLUMNS.map((c) => c.header), COLUMNS.map((c) => c.example)]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const sheet = await readSpreadsheet(file);
      if (sheet.headers.length === 0) throw new Error('That file has no header row.');

      const mapped = sheet.headers.map((h) => BY_KEY.get(key(h)) ?? null);
      if (!mapped.some((c) => c?.required)) {
        throw new Error('That file has no Name column — download the template to see what is expected.');
      }

      const rows: Row[] = [];
      const problems: Parsed['problems'] = [];
      let blank = 0;

      sheet.rows.forEach((cells, i) => {
        const row: Row = {};
        let hasName = false;
        mapped.forEach((column, c) => {
          if (!column) return;
          const cell = (cells[c] ?? '').trim();
          if (!cell) return;
          if (column.required) hasName = true;
          if (column.parse) {
            const out = column.parse(cell);
            if ('error' in out) {
              /* The cell is dropped, not the client: a mistyped revenue is
                 not a reason to refuse somebody's whole account. */
              problems.push({ row: i + 1, message: `${out.error} — imported without it`, dropped: false });
            }
            else row[column.field] = out.value;
          } else {
            row[column.field] = cell;
          }
        });
        /* A trailing empty line is what a spreadsheet always has; it is not a
           problem to report, it is a row that is not there. */
        if (!hasName) {
          if (cells.some((v) => v.trim())) {
            problems.push({ row: i + 1, message: 'No name — row skipped', dropped: true });
          }
          else blank += 1;
          return;
        }
        rows.push(row);
      });

      setParsed({
        fileName: file.name,
        rows,
        unknown: sheet.headers.filter((h) => !BY_KEY.has(key(h))).filter(Boolean),
        problems,
        blank,
      });
    } catch (e) {
      setParsed(null);
      setError(e instanceof Error ? e.message : 'Could not read that file');
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<ClientImportResult>('/am/clients/import', { rows: parsed.rows });
      setResult(r);
      setParsed(null);
      onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not import those clients');
    } finally {
      setBusy(false);
    }
  };

  const trouble = result?.rows.filter((r) => r.outcome === 'skipped' || r.note) ?? [];

  return (
    <Modal
      title="Import clients from a spreadsheet"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {result ? 'Done' : 'Cancel'}
          </button>
          {parsed && parsed.rows.length > 0 && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run()}>
              {busy ? 'Importing…' : `Import ${parsed.rows.length} client${parsed.rows.length === 1 ? '' : 's'}`}
            </button>
          )}
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      {result && (
        <>
          <p className="small">
            <strong>{result.created}</strong> client{result.created === 1 ? '' : 's'} created
            {result.skipped > 0 ? `, ${result.skipped} skipped` : ''}.
          </p>
          {trouble.length > 0 && (
            <div className="table-wrap mt-4">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Row</th>
                    <th>Client</th>
                    <th>What happened</th>
                  </tr>
                </thead>
                <tbody>
                  {trouble.map((r) => (
                    <tr key={r.row}>
                      <td className="xs mono">{r.row}</td>
                      <td className="xs">{r.name || <span className="subtle">—</span>}</td>
                      <td className="xs">{r.reason ?? r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!result && (
        <>
          <p className="small">
            One client per row. Only <strong>Name</strong> is required, and a name already on the
            client list is left alone rather than duplicated. .csv or .xlsx, and the column headings
            are matched however they are capitalised or spaced.
          </p>

          <div className="row wrap mt-4" style={{ gap: 'var(--space-3)' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={template}>
              Download the template
            </button>
            <label className="btn btn-ghost btn-sm">
              {parsed ? 'Choose a different file' : 'Choose a file'}
              <input
                type="file"
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  void pick(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {busy && !parsed && <span className="xs muted">Reading…</span>}
          </div>

          {parsed && (
            <div className="mt-4">
              <p className="small">
                <strong>{parsed.fileName}</strong> — {parsed.rows.length} client
                {parsed.rows.length === 1 ? '' : 's'} ready
                {parsed.blank > 0 ? `, ${parsed.blank} empty row${parsed.blank === 1 ? '' : 's'} ignored` : ''}
                {parsed.problems.some((x) => x.dropped)
                  ? `, ${parsed.problems.filter((x) => x.dropped).length} skipped`
                  : ''}
                {parsed.problems.some((x) => !x.dropped)
                  ? `, ${parsed.problems.filter((x) => !x.dropped).length} cell${
                      parsed.problems.filter((x) => !x.dropped).length === 1 ? '' : 's'
                    } to check`
                  : ''}
                .
              </p>
              {parsed.unknown.length > 0 && (
                <p className="xs muted mt-2">
                  Ignored column{parsed.unknown.length === 1 ? '' : 's'}: {parsed.unknown.join(', ')}.
                </p>
              )}
              {/* Said before anything is written, so the file can be fixed
                  rather than half-imported and reconciled afterwards. */}
              {parsed.problems.length > 0 && (
                <div className="table-wrap mt-2">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Row</th>
                        <th>What will happen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.problems.slice(0, 50).map((p, i) => (
                        <tr key={`${p.row}-${i}`}>
                          <td className="xs mono">{p.row}</td>
                          <td className="xs">{p.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <span className="eyebrow">The columns</span>
            <div className="table-wrap mt-2">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '22%' }}>Heading</th>
                    <th style={{ width: '28%' }}>Example</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {COLUMNS.map((c) => (
                    <tr key={c.header}>
                      <td className="xs strong">{c.header}</td>
                      <td className="xs mono">{c.example}</td>
                      <td className="xs muted">{c.hint ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
